import { CardTable, type CardColumn } from '@/components/CardTable';
import type { JournalEntryRow } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Columns';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { FC, memo, ReactNode, useCallback } from 'react';
import styles from './JournalInfoCards.module.scss';

export type Props = {
  columns: CardColumn<JournalEntryRow>[];
  data: JournalEntryRow[];
  /**
   * Grand total of journal entries, for the card header's `{{index}}/{{total}}`
   * counter. NOT `data.length`: while collapsed, `data` only holds the first
   * 10 rows (see `useJournalRemaining`), but the counter must still read
   * against the full count. Defaults to `data.length` when omitted (e.g. in
   * isolated tests).
   */
  totalRecords?: number;
  /** Action placed below the card list (e.g. 「残りをすべて表示」). */
  footer?: ReactNode;
};

/**
 * Card rendering of 仕訳, one card per journal line. Debit/credit fields are
 * grouped side-by-side via `CardColumn.group` (see `Table/Columns/columns.tsx`
 * and `CardTable`'s `RecordCard`).
 */
export const JournalInfoCards: FC<Props> = memo(
  ({ columns, data, totalRecords, footer }) => {
    const { t } = useTranslation();

    const counterTotal = totalRecords ?? data.length;

    const renderHeader = useCallback(
      // No title on the Journal card (unlike Transactions' 明細番号) — the
      // counter is the only header content, so it goes in the `counter` slot
      // (right-aligned) rather than `title` (left-aligned) to match the design.
      // The grand total (not the rendered-array length CardTable would pass
      // as its own `total` arg) is read from the closure so it stays correct
      // while the list is collapsed to the first 10 rows.
      (_record: JournalEntryRow, index: number) => ({
        counter: t('journal_entry.card_counter', {
          index: toCommaSeparated(index + 1),
          total: toCommaSeparated(counterTotal),
        }),
      }),
      [t, counterTotal]
    );

    return (
      <div className={styles.container} data-testid='journal-info-cards'>
        <CardTable<JournalEntryRow>
          columns={columns}
          data={data}
          columnsPerRow={2}
          header={renderHeader}
          emptyMessage={t('journal_info_empty_message')}
          stickyHeader={true}
          windowScroll={true}
          testId='journal-info-cards'
          footer={footer}
        />
      </div>
    );
  }
);
JournalInfoCards.displayName = 'JournalInfoCards';
